import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtTime } from "@/lib/api";
import { Bell } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

/**
 * Polls staff notifications and toasts new table / waiter-call alerts.
 */
export default function StaffAlerts() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const seen = useRef(new Set());
  const primed = useRef(false);
  const nav = useNavigate();

  const refresh = useCallback(async () => {
    try {
      const [{ data: list }, { data: uc }] = await Promise.all([
        api.get("/notifications", { params: { limit: 30 } }),
        api.get("/notifications/unread-count"),
      ]);
      setItems(list);
      setCount(uc.count || 0);

      if (!primed.current) {
        list.forEach((n) => seen.current.add(n.id));
        primed.current = true;
        return;
      }
      for (const n of list) {
        if (seen.current.has(n.id)) continue;
        seen.current.add(n.id);
        if (n.type === "table" || (n.title || "").toLowerCase().includes("table")) {
          toast(n.title, {
            description: n.message,
            action: {
              label: "Tables",
              onClick: () => nav("/app/tables"),
            },
          });
        }
      }
    } catch {
      /* ignore while logged out */
    }
  }, [nav]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [refresh]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    refresh();
  };

  const markOne = async (nid) => {
    await api.post(`/notifications/${nid}/read`);
    refresh();
  };

  return (
    <div className="relative" data-testid="staff-alerts">
      <button
        type="button"
        onClick={() => { setOpen((o) => !o); refresh(); }}
        className="relative p-2 rounded-md hover:bg-[var(--bg-secondary)] transition-colors"
        aria-label="Alerts"
        data-testid="alerts-bell"
      >
        <Bell size={20} weight="duotone" />
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--brand)] text-white text-[10px] font-bold grid place-items-center">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <>
          <button type="button" className="fixed inset-0 z-40" aria-label="Close" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto z-50 bg-white border border-[#EAE7E0] rounded-lg shadow-lg p-2" data-testid="alerts-panel">
            <div className="flex items-center justify-between px-2 py-1.5">
              <span className="text-xs font-bold uppercase tracking-wider">Alerts</span>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={markAll}>Mark all read</Button>
            </div>
            {items.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-[var(--text-secondary)]">No alerts yet</div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => { markOne(n.id); setOpen(false); if (n.type === "table") nav("/app/tables"); }}
                className={`w-full text-left px-3 py-2 rounded-md hover:bg-[var(--bg-secondary)] ${n.is_read ? "opacity-60" : ""}`}
              >
                <div className="text-sm font-semibold">{n.title}</div>
                <div className="text-xs text-[var(--text-secondary)]">{n.message}</div>
                <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">{fmtTime(n.created_at)}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
